/**
 * CommandPalette — Globale Schnellsuche & Befehls-Palette
 *
 * Aufruf via `Strg+K` / `⌘+K` aus jeder Seite.
 * Sucht serverseitig über Mitarbeiter, Standorte, Niederlassungen, Räume,
 * Stellenausschreibungen. Statische Aktionen (Neuer Mitarbeiter, Papierkorb
 * öffnen etc.) sind immer verfügbar.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator, CommandShortcut,
} from './command';
import {
  User, MapPin, Building2, DoorOpen, Briefcase, Car, Wrench,
  Plus, Trash2, ShieldCheck, CalendarDays, Receipt, Settings,
} from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const STATIC_ACTIONS = [
  { id: 'goto-employees', label: 'Alle Mitarbeiter', icon: User, url: '/employees', keywords: 'mitarbeiter personal', perm: ['employees', 'view'] },
  { id: 'new-employee', label: 'Neuer Mitarbeiter', icon: Plus, url: '/employees?new=1', keywords: 'neu anlegen einstellen', perm: ['employees', 'edit'] },
  { id: 'goto-locations', label: 'Standorte verwalten', icon: MapPin, url: '/locations', keywords: 'standort filiale niederlassung', perm: ['settings', 'view'] },
  { id: 'goto-rooms', label: 'Raumverwaltung', icon: DoorOpen, url: '/rooms', keywords: 'raum buchung', perm: ['rooms', 'view'] },
  { id: 'goto-fleet', label: 'Fuhrpark', icon: Car, url: '/fuhrpark', keywords: 'fahrzeug auto kfz fuhrpark', perm: ['fleet', 'view'] },
  { id: 'goto-facility', label: 'Haustechnik / Meldungen', icon: Wrench, url: '/haustechnik', keywords: 'facility meldung reparatur haustechnik', perm: ['facility', 'view'] },
  { id: 'goto-jobs', label: 'Stellenausschreibungen', icon: Briefcase, url: '/stellenausschreibungen', keywords: 'stelle job bewerbung', perm: ['job_postings', 'view'] },
  { id: 'new-vacation', label: 'Neuer Urlaubsantrag', icon: CalendarDays, url: '/vacations?new=1', keywords: 'urlaub abwesenheit' },
  { id: 'goto-payroll', label: 'Lohnabrechnungen', icon: Receipt, url: '/payroll', keywords: 'gehalt lohn', adminOnly: true },
  { id: 'goto-audit', label: 'Zugriffsprotokolle öffnen', icon: ShieldCheck, url: '/audit-log', keywords: 'audit log protokoll', adminOnly: true },
  { id: 'goto-trash', label: 'Papierkorb öffnen', icon: Trash2, url: '/admin/trash', keywords: 'gelöscht wiederherstellen', adminOnly: true },
  { id: 'goto-settings', label: 'Einstellungen', icon: Settings, url: '/settings', keywords: 'profil konto' },
];

const GROUP_META = {
  employees:        { label: 'Mitarbeiter', icon: User },
  locations:        { label: 'Standorte', icon: MapPin },
  branches:         { label: 'Niederlassungen', icon: Building2 },
  rooms:            { label: 'Räume', icon: DoorOpen },
  vehicles:         { label: 'Fahrzeuge', icon: Car },
  facility_tickets: { label: 'Facility-Meldungen', icon: Wrench },
  job_postings:     { label: 'Stellenausschreibungen', icon: Briefcase },
};

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { isAdmin, hasPermission } = useAuth();

  // Strg+K / ⌘+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Debounced server search
  useEffect(() => {
    if (!open) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults({});
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/search', { params: { q: trimmed, limit: 6 } });
        setResults(res.data?.results || {});
      } catch {
        setResults({});
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(timer);
  }, [query, open]);

  const visibleActions = useMemo(
    () => STATIC_ACTIONS.filter((a) => {
      if (a.adminOnly && !isAdmin) return false;
      if (a.perm && !hasPermission(a.perm[0], a.perm[1])) return false;
      return true;
    }),
    [isAdmin, hasPermission],
  );

  const goTo = (url) => {
    setOpen(false);
    setQuery('');
    navigate(url);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Mitarbeiter, Standort, Raum suchen oder Aktion ausführen..."
        value={query}
        onValueChange={setQuery}
        data-testid="command-palette-input"
      />
      <CommandList className="max-h-[400px]">
        <CommandEmpty>
          {loading ? 'Suche läuft…' : query.length < 2 ? 'Tippe mindestens 2 Zeichen…' : 'Keine Treffer.'}
        </CommandEmpty>

        {/* Server-side Suchergebnisse */}
        {Object.entries(results).map(([key, items]) => {
          if (!items?.length) return null;
          const meta = GROUP_META[key] || { label: key, icon: null };
          const Icon = meta.icon;
          return (
            <CommandGroup key={key} heading={meta.label}>
              {items.map((item) => (
                <CommandItem
                  key={`${key}-${item.id}`}
                  value={`${key}-${item.label}-${item.subtitle}`}
                  onSelect={() => goTo(item.url)}
                  data-testid={`command-result-${key}-${item.id}`}
                >
                  {Icon && <Icon className="mr-2 h-4 w-4" />}
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{item.label}</p>
                    {item.subtitle && (
                      <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}

        {/* Statische Aktionen */}
        {(query.length < 2 || visibleActions.some(a => (a.label + a.keywords).toLowerCase().includes(query.toLowerCase()))) && (
          <>
            {Object.values(results).some((arr) => arr?.length) && <CommandSeparator />}
            <CommandGroup heading="Aktionen">
              {visibleActions.map((action) => {
                const Icon = action.icon;
                return (
                  <CommandItem
                    key={action.id}
                    value={`${action.label} ${action.keywords}`}
                    onSelect={() => goTo(action.url)}
                    data-testid={`command-action-${action.id}`}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    {action.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />
        <div className="px-2 py-1.5 text-[11px] text-muted-foreground flex items-center justify-between">
          <span>Tipp: <CommandShortcut>↑↓</CommandShortcut> Navigieren · <CommandShortcut>↵</CommandShortcut> Öffnen</span>
          <span><CommandShortcut>Esc</CommandShortcut> Schließen</span>
        </div>
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
